import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Crown, Sparkles } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const PREMIUM_FEATURES = [
  "Unlimited eligible vocal recordings", "Full professional vocal mixer", "Advanced effects (EQ, reverb, delay, compression)",
  "Premium vocal presets", "High-quality WAV rendering", "Eligible downloads", "More creator tools",
];

export default function Premium() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => { api.get("/billing/plans").then(({ data }) => setPlans(data.plans)); }, []);
  const monthly = plans.find((p) => p.plan === "monthly");
  const annual = plans.find((p) => p.plan === "annual");

  const checkout = async (plan) => {
    if (!user) { nav("/login", { state: { from: "/premium" } }); return; }
    setBusy(plan);
    try {
      const { data } = await api.post("/billing/checkout", { plan, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e)); setBusy(""); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
      <div className="text-center mb-12">
        <Crown className="w-10 h-10 text-neon-pink mx-auto mb-4" />
        <h1 className="font-head text-4xl sm:text-5xl font-black tracking-tighter">Take Your Voice Further</h1>
        <p className="text-muted-foreground mt-3 max-w-lg mx-auto">Unlock unlimited recordings and the full professional studio.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Free */}
        <div className="card-surface rounded-3xl p-8">
          <h3 className="font-head text-xl font-bold">Free</h3>
          <p className="text-4xl font-head font-black mt-3">CA$0</p>
          <p className="text-sm text-muted-foreground mb-6">Forever</p>
          <ul className="space-y-3 text-sm">
            {["2 free vocal recordings", "Browse & search catalog", "Save songs", "Community access", "Follow singers"].map((f) => (
              <li key={f} className="flex items-center gap-2 text-muted-foreground"><Check className="w-4 h-4 text-white/40" /> {f}</li>
            ))}
          </ul>
        </div>

        {/* Premium */}
        <div className="relative rounded-3xl p-8 glass border-2 border-neon-pink/50 glow-pink">
          <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-neon-pink text-xs font-bold px-4 py-1 rounded-full">BEST VALUE</span>
          <h3 className="font-head text-xl font-bold flex items-center gap-2"><Sparkles className="w-5 h-5 text-neon-pink" /> Premium</h3>

          <div className="flex items-end gap-2 mt-4">
            <p className="text-4xl font-head font-black">CA${annual ? annual.amount : "46.99"}</p>
            <span className="text-muted-foreground mb-1.5">/year</span>
          </div>
          <p className="text-sm text-neon-cyan">Just CA${annual ? (annual.amount / 12).toFixed(2) : "3.92"}/mo — billed annually</p>
          <Button onClick={() => checkout("annual")} disabled={busy} data-testid="checkout-annual" className="w-full rounded-full bg-neon-pink hover:bg-neon-pink/90 font-bold py-6 mt-4">{busy === "annual" ? "Redirecting..." : "Go Annual — Best Value"}</Button>

          <div className="flex items-center gap-3 my-4"><div className="flex-1 h-px bg-white/10" /><span className="text-xs text-muted-foreground">or</span><div className="flex-1 h-px bg-white/10" /></div>

          <div className="flex items-end gap-2">
            <p className="text-2xl font-head font-bold">CA${monthly ? monthly.amount : "11.99"}</p>
            <span className="text-muted-foreground mb-1">/month</span>
          </div>
          <Button onClick={() => checkout("monthly")} disabled={busy} variant="outline" data-testid="checkout-monthly" className="w-full rounded-full glass border-white/20 font-bold py-6 mt-3">{busy === "monthly" ? "Redirecting..." : "Go Monthly"}</Button>

          <ul className="space-y-3 text-sm mt-7">
            {PREMIUM_FEATURES.map((f) => <li key={f} className="flex items-center gap-2"><Check className="w-4 h-4 text-neon-pink" /> {f}</li>)}
          </ul>
        </div>
      </div>

      <p className="text-center text-xs text-muted-foreground mt-8 max-w-xl mx-auto">
        Subscriptions renew automatically until cancelled. Annual subscribers receive a renewal reminder ~15 days before renewal. Cancel anytime — access continues to end of billing period. Taxes may apply. Payments are processed securely by Stripe; SINGVI never stores your card number.
      </p>
    </div>
  );
}
